import { useCallback, useEffect, useState } from 'react'
import { FileRejection, useDropzone } from 'react-dropzone'

export type FileState = {
  data?: unknown
  file?: File
  invalid: boolean
  loading: boolean
  name?: string
  loaded?: number
  total?: number
  url?: File | string
}

export type UseDropzoneUploadProps<TFile> = {
  accept?: string | string[]
  defaultValue?: unknown
  disabled?: boolean
  getName?: (file: TFile) => string
  getUrl?: (file: TFile) => string | File
  maxFiles?: number
  maxSize?: number
  minSize?: number
  multiple?: boolean
  noClick?: boolean
  noKeyboard?: boolean
  value?: unknown
  onChange?: (value: unknown) => void
  onUpload?: (files: File[]) => Promise<TFile[]>
}

function defaultGetUrl(data: unknown): string | File {
  return data instanceof File ? data : ''
}

function defaultGetName(data: unknown): string {
  return data instanceof File ? data.name : ''
}

function defaultUploadHandler<T extends unknown>(files: File[]): Promise<T[]> {
  return Promise.resolve(files as T[])
}

export function useDropzoneUpload<TFile = unknown>({
  accept,
  defaultValue,
  disabled,
  getName = defaultGetName,
  getUrl = defaultGetUrl,
  maxFiles,
  maxSize,
  minSize,
  value,
  multiple,
  onChange,
  onUpload = defaultUploadHandler,
}: UseDropzoneUploadProps<TFile>) {
  const getFilesState = useCallback(
    (v?: unknown): FileState[] => {
      const arr = (Array.isArray(v) && v) || (v != null && [v]) || []

      return arr.map((data) => ({
        data,
        invalid: false,
        loading: false,
        name: getName(data),
        url: getUrl(data),
      }))
    },
    [getName, getUrl]
  )

  const [filesState, setFilesState] = useState<FileState[]>(() => getFilesState(defaultValue))

  const updateFilesState = useCallback(
    (data?: unknown) => {
      setFilesState((prevState) => {
        const dataFilesState = getFilesState(data)
        const currState = [...prevState].filter((v) => v.data == null)
        return multiple ? [...currState, ...dataFilesState] : dataFilesState
      })
    },
    [getFilesState, multiple]
  )

  const changeFiles = useCallback(
    (v: unknown) => {
      // call onChange if controlled input, otherwise change internal state directly
      if (onChange && typeof value !== 'undefined') {
        onChange(v)
      } else {
        updateFilesState(v)
      }
    },
    [onChange, updateFilesState, value]
  )

  useEffect(() => {
    if (typeof value !== 'undefined') {
      updateFilesState(value)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value])

  function handleDropAccepted(droppedFiles: File[]): void {
    const loadingState = droppedFiles.map((file) => ({
      file,
      name: file.name,
      invalid: false,
      loading: true,
    }))

    setFilesState((prevState) => [...prevState, ...loadingState])

    onUpload(droppedFiles)
      .then((data) => {
        setFilesState((prevState) => {
          const currState = [...prevState]

          // validate next value and call change handler
          if (multiple) {
            const currData = currState.filter((v) => v.data != null).map((v) => v.data)
            changeFiles([...currData, ...data])
          } else {
            changeFiles(data[0])
          }

          // remove intermediate loading state of current files from filesState
          return currState.filter((v) => !v.file || !droppedFiles.includes(v.file))
        })
      })
      .catch(() => {
        // replace intermediate loading state of current files with invalid state
        setFilesState((currState) =>
          currState.map((item) =>
            item.file && droppedFiles.includes(item.file) ? { ...item, loading: false, invalid: true } : item
          )
        )
      })
  }

  function handleDropRejected(fileRejections: FileRejection[]): void {
    const rejectedFilesState: FileState[] = fileRejections.map((r) => ({
      file: r.file,
      name: r.file.name,
      invalid: true,
      loading: false,
    }))

    setFilesState((currState) => (multiple ? [...currState, ...rejectedFilesState] : rejectedFilesState))
  }

  const { getInputProps, getRootProps, ...dzOptions } = useDropzone({
    accept,
    disabled,
    maxFiles,
    maxSize,
    minSize,
    multiple,
    onDropAccepted: handleDropAccepted,
    onDropRejected: handleDropRejected,
  })

  const remove = useCallback(
    (index: number) => {
      const nextFilesState = multiple ? filesState.filter((_, idx) => idx !== index) : []

      const nextData = multiple
        ? nextFilesState.filter((item) => typeof item.data !== 'undefined').map((item) => item.data)
        : null

      setFilesState(nextFilesState)
      changeFiles(nextData)
    },
    [changeFiles, filesState, multiple]
  )

  return { ...dzOptions, filesState, getInputProps, getRootProps, remove }
}
